/**
 * usePreisspiegelTiles (HOF-271) — the Preisspiegel grid as ONE read.
 *
 * **The defect.** The page issued two requests that could not overlap.
 * `usePriceIndex` answered the shell's question — which items exist, and as of
 * when — and only then could `useItemPriceSeries` be called, because its item set
 * was derived from that answer (#2694). On WF over the seven days to 2026-09-03
 * that was p50 3.609 ms + 1.937 ms in series: ~5,5 s to tiles, ~12 s at p90.
 *
 * **Why the second read had to wait, and why it no longer does.** Its scope was a
 * fact about DATA — which items have observations — knowable only after the first
 * answer. But the grid never needed that: it needs the items of the OPEN
 * KATEGORIE, which is a fact about the CATALOG. The backend now reads the same
 * `config/catalog/preisspiegel@v1.yaml` this app compiles (#2771), so both legs
 * ride one `asyncio.gather` behind `GET /twin/preisspiegel/tiles`. An item with
 * no data contributes no cells — exactly as it did when the client filtered it
 * out first.
 *
 * **The derivation is not a copy.** Both halves go through the SAME builders the
 * two standalone hooks use (`buildPriceIndexResult`,
 * `buildItemPriceSeriesResult`), so the tile a reader sees is computed by one
 * body of code with three callers rather than two that could drift. Drift here
 * would be invisible: both copies produce a plausible page.
 *
 * **The index survives a scope change; the cells do not.** That asymmetry is the
 * one piece of state this hook holds, and it is sound rather than convenient: the
 * index leg is CATALOG-WIDE by contract (pinned server-side by
 * `test_the_index_leg_stays_catalog_wide_even_though_the_cells_leg_is_not`), so
 * the previous scope's index IS this scope's index — it is not stale data, it is
 * the same data. Carrying it is what stops the Warengruppe lens blanking for a
 * frame every time the reader drills into a tile. The cells are carried nowhere,
 * for the reason `useItemPriceSeries` already documents: a previous Kategorie's
 * cells under this Kategorie's tiles would render "Keine Daten" with the
 * confidence of a read that had covered them.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  twinService,
  type PreisspiegelDerivedTile,
  type PreisspiegelIndexResponse,
  type PreisspiegelTileHeadlinesResponse,
  type PreisspiegelTileHistoriesResponse,
  type PreisspiegelTileSummary,
  type PreisspiegelTilesResponse,
} from '../services/twinService';
import {
  buildItemPriceSeriesResult,
  normaliseItemIds,
  useCellDriftWarnings,
  type UseItemPriceSeriesResult,
} from './useOwnPriceSeries';
import {
  buildPriceIndexResult,
  useIndexDriftWarnings,
  type UsePriceIndexResult,
} from './usePriceIndex';

/**
 * What the cell half covers. Exactly one of the two is meaningful at a time,
 * which mirrors the page: a tile grid is a Kategorie, a drilled-in chart is one
 * item.
 *
 * `kat: null` with no items is the COLD visit — no `?kat=`, no stored
 * preference — and the server resolves it. That is a real request shape, not a
 * "not ready yet": suppressing it would put the round trip back exactly where
 * this issue removed it.
 */
export interface PreisspiegelTilesScope {
  kat?: string | null;
  items?: readonly string[];
}

export const preisspiegelTilesKeys = {
  all: ['preisspiegel', 'tiles'] as const,
  /**
   * ⚠️ The key carries the SCOPE, and it must.
   *
   * The cell half differs per scope while the index half does not, so one entry
   * shared across scopes would serve one Kategorie's prices under another's
   * tiles — and `data_quality` travels with the entry, so a cap that fired while
   * reading fifteen items would silently qualify a chart drawn from one. This is
   * `ownPriceKeys.cellsForItems`'s reasoning, unchanged: deliberately NOT
   * subset-aware.
   */
  scope: ({ kat, items }: PreisspiegelTilesScope) =>
    [...preisspiegelTilesKeys.all, 'scope', items?.length ? normaliseItemIds(items) : null, kat ?? null] as const,
  /**
   * HOF-377 — the headline read's own entry, under the SAME scope discipline.
   *
   * Its own key rather than the scope key, because the two hold different bodies
   * for the same question: one carries `points` and the live cells, the other
   * neither. Sharing an entry would serve whichever landed last.
   */
  headlines: (scope: PreisspiegelTilesScope) => [...preisspiegelTilesKeys.scope(scope), 'headlines'] as const,
  /**
   * The curve, keyed on the RESOLVED scope rather than the requested one.
   *
   * That is the point of fetching it second: on a cold visit the requested scope is
   * `{kat: null}` and the server resolves first-enabled, so keying on the request
   * would key two different Kategorien's curves the same. Keyed on the answer, the
   * entry is reusable the moment the page stamps `?kat=<resolved>` into the URL.
   */
  histories: (scope: PreisspiegelTilesScope) => [...preisspiegelTilesKeys.scope(scope), 'histories'] as const,
};

export interface UsePreisspiegelTilesResult {
  /** The shell's half — identical in shape and derivation to `usePriceIndex()`. */
  index: UsePriceIndexResult;
  /** The price half — identical in shape and derivation to `useItemPriceSeries()`. */
  cells: UseItemPriceSeriesResult;
  /**
   * HOF-270 — the materialised summary per item, and who has none.
   *
   * `summaryFor` returns `undefined` for two quite different items and the caller
   * must not conflate them: one the farm has no data for (the ordinary empty
   * tile) and one whose summary is MISSING (`fallbackItems`), which the page
   * draws from the cells instead. Only the second means the materialiser is
   * behind, which is why the server names it rather than leaving it to be
   * inferred from an absence.
   */
  summaryFor: (itemId: string) => PreisspiegelTileSummary | undefined;
  /** Items the page must build the old way, from the cells. */
  fallbackItems: readonly string[];
  /**
   * HOF-377 — the MATERIALISED benchmark presence bit for an item, in the drawn
   * vocabulary (`consumer` / `trade`).
   *
   * The tile uses exactly this to decide whether an own-empty item is nonetheless
   * openable and what its scope line says. It used to be derived from benchmark
   * SERIES the page had fetched, which is what the grid paid 5-8 UDB aggregates of
   * 2-5 s each for on every visit; now it rides the summary. Empty for an item with
   * no summary and on a `/tiles` response that predates the field, which reads as
   * "no benchmark" — exactly what such a server would have said.
   */
  benchmarkLevelsFor: (itemId: string) => readonly string[];
  /**
   * Whether the 120-day curves have landed yet — and, if not, WHY.
   *
   * `'loading'` is first paint: every tile has its title, price, *Stand* and delta
   * and the sparkline slot holds a placeholder, so the grid does not reflow when
   * the curve arrives (the `#2420` skeleton property). `'ready'` on the `/tiles`
   * path, where the points ride the same response they always did.
   *
   * `'unavailable'` is codex round 2's P1, and it is the state that must not be
   * collapsed into either of the others. A histories read that FAILED — or came
   * back soft-failed or truncated (`#2573`) — yields no points for the items it
   * did not cover, and reporting that as `'ready'` draws a genuine 120-day history
   * as an empty curve with nothing anywhere saying so. It is the same distinction
   * `PriceAvailability` already makes one layer up: absence carries no information
   * until a read has actually answered.
   */
  sparklines: 'ready' | 'loading' | 'unavailable';
  /**
   * The Kategorie the server actually read cells for.
   *
   * On a cold visit this is the server's first-enabled resolution and the page
   * adopts it, which is what keeps that visit at one request. `null` while the
   * read is in flight, and for a tenant with no data at all.
   */
  resolvedKat: string | null;
  /**
   * HOF-299 S3 — the tenant's DERIVED leaf tiles (its own `product` entities), the
   * whole roster, as the server sent them. Carried across scope changes exactly
   * like the index and for the same reason: the roster is tenant-wide, not
   * scope-wide, so the previous scope's answer IS this scope's answer — and
   * dropping it between two reads would blank every product tile in the lens for
   * a frame. The page turns them into `TrackedItem`s (`servedTilesFromWire`) and
   * merges them into its effective catalog. Empty for a tenant with none and on
   * a backend that predates the field.
   */
  derivedTiles: readonly PreisspiegelDerivedTile[];
}

const NO_DERIVED_TILES: readonly PreisspiegelDerivedTile[] = [];

export function usePreisspiegelTiles(scope: PreisspiegelTilesScope): UsePreisspiegelTilesResult {
  const kat = scope.kat ?? null;
  const items = scope.items;

  // HOF-377 — the GRID takes the split pair; the drill-in keeps `/tiles` (AC 15).
  //
  // The chart is the one surface the summaries deliberately do not serve: it is
  // item-scoped and live by design, and `/tiles` is where its cells and its
  // benchmark lines come from. Routing it through the headline pair would remove
  // exactly the reads it exists to make.
  const drilled = items !== undefined;

  const headlines = useQuery({
    queryKey: preisspiegelTilesKeys.headlines({ kat, items }),
    queryFn: () => twinService.getPreisspiegelTileHeadlines({ kat, items }),
    enabled: !drilled,
    // HOF-93's 300 s, for the reason `/tiles` already documents: the index's job is
    // to NOTICE a new item, not to notice it instantly, against an ingest cadence
    // measured in hours.
    staleTime: 300_000,
    gcTime: 1_800_000,
  });

  // The TENANT-level fallback, and the only one there is (spec Assumption 3).
  //
  // The writer now emits a row for every item with prices OR benchmark coverage, so
  // an item absent from the headline response has nothing to summarise BY
  // CONSTRUCTION — there is no per-item fallback left to make. What survives is
  // "this tenant has prices and no drawable summaries at all", which is a
  // materialiser that has not run, and the server names it rather than leaving it
  // to be inferred. A headline read that FAILED takes the same route: `/tiles` is
  // the path that has always worked, and falling back to it is what keeps a farmer
  // looking at prices rather than at „Keine Daten".
  const headlinesFellBack = Boolean(headlines.data?.summaries?.grid_fallback) || headlines.isError;

  const tiles = useQuery({
    queryKey: preisspiegelTilesKeys.scope({ kat, items }),
    queryFn: () => twinService.getPreisspiegelTiles({ kat, items }),
    enabled: drilled || headlinesFellBack,
    staleTime: 300_000,
    gcTime: 1_800_000,
    // Deliberately NO `placeholderData: keepPreviousData`, unchanged: it would carry
    // the previous scope's CELLS across a key change and render "Keine Daten" over
    // items the read has not covered yet.
  });

  // The curve, on its own request, keyed on the RESOLVED scope. Fired as soon as the
  // headline response exists — the requirement is that first paint never WAITS for
  // it, not that it is lazy about starting (spec Assumption 5).
  const resolvedFromHeadlines = headlines.data?.kat ?? null;
  const historyScope = useMemo(
    () => ({ kat: resolvedFromHeadlines, items: headlines.data?.items }),
    [resolvedFromHeadlines, headlines.data],
  );
  const histories = useQuery({
    queryKey: preisspiegelTilesKeys.histories(historyScope),
    queryFn: () => twinService.getPreisspiegelTileHistories({ kat: resolvedFromHeadlines }),
    enabled: !drilled && !headlinesFellBack && Boolean(headlines.data),
    staleTime: 300_000,
    gcTime: 1_800_000,
  });

  const usingTiles = drilled || headlinesFellBack;
  const active = usingTiles ? tiles : headlines;
  const error = (active.error as Error | undefined) ?? null;
  const isLoading = active.isLoading || (!usingTiles && headlines.isLoading);

  // The headline response wearing `/tiles`' envelope, so everything below this line
  // — and the whole page — is unchanged by which route answered. The server already
  // sends `index` and an empty-but-complete `cells` in that envelope; what is merged
  // here is the one thing that cannot be: the curve, which arrives separately.
  const merged: PreisspiegelTilesResponse | undefined = useMemo(() => {
    if (usingTiles) return tiles.data;
    const answer = headlines.data;
    if (!answer) return undefined;
    return {
      kat: answer.kat,
      items: answer.items,
      index: answer.index,
      cells: answer.cells,
      summaries: {
        tiles: answer.summaries.tiles.map((tile) => mergeHistory(tile, histories.data)),
        // Empty by construction, not by omission — see `headlinesFellBack`.
        fallback_items: [],
        data_quality: {
          summaries_unavailable: answer.summaries.data_quality.summaries_unavailable,
          truncated: answer.summaries.data_quality.truncated,
          fallback_count: 0,
        },
      },
      derived_tiles: answer.derived_tiles,
    };
  }, [usingTiles, tiles.data, headlines.data, histories.data]);

  // The index half, carried across scope changes. See the module docstring for
  // why that is sound here and wrong for the cells: this leg is catalog-wide by
  // contract, so the previous scope's answer is not stale, it is the same
  // answer. Without it the lens blanks for a frame on every drill-in.
  const [carriedIndex, setCarriedIndex] = useState<PreisspiegelIndexResponse | undefined>(undefined);
  useEffect(() => {
    if (merged?.index) setCarriedIndex(merged.index);
  }, [merged]);
  const indexData = merged?.index ?? carriedIndex;

  // HOF-299 S3 — the derived roster, carried like the index (see the result type).
  const [carriedDerived, setCarriedDerived] = useState<readonly PreisspiegelDerivedTile[] | undefined>(undefined);
  useEffect(() => {
    if (merged?.derived_tiles) setCarriedDerived(merged.derived_tiles.items);
  }, [merged]);
  const derivedTiles = merged?.derived_tiles?.items ?? carriedDerived ?? NO_DERIVED_TILES;

  const index = useMemo(
    () =>
      buildPriceIndexResult(indexData, {
        // Holding a carried index is holding an answer. Reporting `isLoading`
        // over one would put the full-page overlay back up on every drill-in,
        // which is the flash this carry exists to remove.
        isLoading: isLoading && indexData === undefined,
        error,
      }),
    [indexData, isLoading, error],
  );

  const cells = useMemo(
    () => buildItemPriceSeriesResult(merged?.cells, { isLoading, error }),
    [merged, isLoading, error],
  );

  // Keyed off the SAME response the cells came from, and carried nowhere — the
  // asymmetry the module docstring gives for the cells applies here for the same
  // reason and more sharply: a previous Kategorie's summaries under this
  // Kategorie's tiles would draw its PRICES, not merely a "Keine Daten".
  const summaries = merged?.summaries;
  const summaryByItem = useMemo(() => {
    const map: Record<string, PreisspiegelTileSummary> = {};
    for (const tile of summaries?.tiles ?? []) map[tile.catalog_item_id] = tile;
    return map;
  }, [summaries]);
  const summaryFor = useCallback((itemId: string) => summaryByItem[itemId], [summaryByItem]);
  const fallbackItems = useMemo(() => summaries?.fallback_items ?? [], [summaries]);
  const benchmarkLevelsFor = useCallback(
    (itemId: string) => summaryByItem[itemId]?.benchmark_levels ?? NO_BENCHMARK_LEVELS,
    [summaryByItem],
  );

  useIndexDriftWarnings(index.entries, indexData?.data_quality);
  useCellDriftWarnings(merged?.cells);

  // The cold visit must cost ONE request, and without this it cost two (codex
  // r1 P1).
  //
  // With no `?kat=`, no `?item=` and no stored preference the page asks for the
  // unresolved scope and the server answers with first-enabled. The page then
  // stamps `?kat=<resolved>` into the URL — which is the point: every visit after
  // the first carries it. But that moves the key from `{kat: null}` to
  // `{kat: '<resolved>'}`, and nothing was ever written at the second key, so
  // React Query issued the whole read a second time. The one visit this issue
  // exists to make cheap was the one paying twice.
  //
  // Seeding rather than widening the key: the key must stay scope-exact (see
  // `preisspiegelTilesKeys.scope` — a subset-aware key serves one Kategorie's
  // prices under another's tiles). What is sound instead is that this answer
  // already IS the answer to the resolved question: the server resolved
  // first-enabled from the very rows it returned, and its `items` are that
  // Kategorie's full item list — the same list a `?kat=` request resolves. So the
  // two requests differ in nothing but the question's spelling.
  //
  // Both entries are seeded, because both routes are keyed on the requested scope
  // and the histories entry already keys on the resolved one.
  const queryClient = useQueryClient();
  const resolvedKat = merged?.kat ?? null;
  useEffect(() => {
    if (items !== undefined) return;
    if (resolvedKat === null || resolvedKat === kat) return;
    if (headlines.data) {
      queryClient.setQueryData(preisspiegelTilesKeys.headlines({ kat: resolvedKat }), headlines.data);
    }
    if (usingTiles && tiles.data) {
      queryClient.setQueryData(preisspiegelTilesKeys.scope({ kat: resolvedKat }), tiles.data);
    }
  }, [headlines.data, tiles.data, usingTiles, resolvedKat, kat, items, queryClient]);

  return {
    index,
    cells,
    summaryFor,
    fallbackItems,
    benchmarkLevelsFor,
    // `/tiles` carries the points on the same response it always did; the split
    // pair does not, until the second request lands — and a read that ANSWERED but
    // fell short is neither of those (codex r2 P1).
    sparklines: usingTiles ? 'ready' : historiesState(histories),
    resolvedKat,
    derivedTiles,
  };
}

const NO_BENCHMARK_LEVELS: readonly string[] = [];

/**
 * What the curve layer can say about the tiles on screen (codex r2 P1).
 *
 * `'unavailable'` covers three shapes that look identical from a tile: the request
 * errored, the server soft-failed its read (`summaries_unavailable`, `#2573`), or it
 * answered a PREFIX (`truncated`) and the items past the bound have no points. All
 * three produce `points: []` for some item, and all three must be distinguishable
 * from "this item has no history", which is what an empty curve means.
 */
function historiesState(
  query: { data?: PreisspiegelTileHistoriesResponse; isError: boolean },
): 'ready' | 'loading' | 'unavailable' {
  if (query.isError) return 'unavailable';
  const quality = query.data?.data_quality;
  if (!quality) return 'loading';
  return quality.summaries_unavailable || quality.truncated || quality.degraded ? 'unavailable' : 'ready';
}

/**
 * One headline row plus its curve, as the `PreisspiegelTileSummary` every consumer
 * below this hook already reads.
 *
 * `points: []` until the histories request lands, and that is deliberate rather
 * than a placeholder value: `tileModelFromSummary` iterates it, and an empty list
 * is the one shape that renders a complete tile with an empty sparkline slot. A
 * `null` there throws and takes the whole grid down.
 *
 * The keys the headline route strips stay ABSENT rather than being filled in: they
 * are optional on `PreisspiegelTileSummary` for exactly this reason, and a `0` or an
 * `''` put there to satisfy a type is a value in the payload that means nothing.
 */
function mergeHistory(
  tile: PreisspiegelTileHeadlinesResponse['summaries']['tiles'][number],
  histories: PreisspiegelTileHistoriesResponse | undefined,
): PreisspiegelTileSummary {
  const history = histories?.histories.find((one) => one.catalog_item_id === tile.catalog_item_id);
  return {
    ...tile,
    points: history?.points ?? [],
    // The window is a property of the CURVE, so it travels with it. `undefined`
    // rather than `0` where the curve has not landed: `tileModelFromSummary`
    // defaults an absent window to `SPARKLINE_TILE_WINDOW_DAYS`, and `0` is not
    // nullish — it would clip every point out of a curve that HAD arrived.
    ...(history?.window_days == null ? {} : { window_days: history.window_days }),
  };
}

/** Re-exported for the page's tests, which assert the request shape. */
export type { PreisspiegelTilesResponse };
